/**
 * Generic Stack Actions Hook
 * 
 * Provides reusable open/close/isOpen methods for any Mantine stack.
 * This eliminates duplication between modal and drawer stack actions.
 * 
 * @example
 * const { open, close, closeAll, isOpen } = useStackActions(modalStack);
 */

import * as React from 'react';
import type { MantineStack, StackId } from './types.js';

/**
 * Generic hook for stack actions (open, close, closeAll, isOpen)
 * 
 * @param stack - Mantine stack instance (from useModalsStack or useDrawersStack)
 * @returns Object with stack action methods
 */
export function useStackActions<T extends StackId>(stack: MantineStack<T>) {
  const open = React.useCallback(
    (id: T) => {
      stack.open(id);
    },
    [stack]
  );

  const close = React.useCallback(
    (id: T) => {
      stack.close(id);
    },
    [stack]
  );

  const closeAll = React.useCallback(() => {
    stack.closeAll();
  }, [stack]);

  const isOpen = React.useCallback(
    (id: T) => {
      return stack.state[id] || false;
    },
    [stack.state]
  );

  return React.useMemo(
    () => ({ open, close, closeAll, isOpen }),
    [open, close, closeAll, isOpen]
  );
}
